import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useLogout, useGetDailySchedule, getGetDailyScheduleQueryKey } from "@workspace/api-client";
import { logout as clearTokens, useAuth } from "@/lib/auth";
import { FeedbackButton } from "@/components/FeedbackButton";
import { useFeatureFlagsContext } from "@/contexts/FeatureFlagsContext";
import { useChangelog } from "@/hooks/use-changelog";
import { NAV_ITEMS, NAV_SECTIONS, type NavSectionId } from "@/lib/navigation";
import { ROLE_PERMISSIONS } from "@/lib/role-permissions";
import { useRolePermissions } from "@/lib/api-extra";
import type { AnyRole } from "@/lib/roles";
import {
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
} from "lucide-react";

export function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { mutate: logoutApi } = useLogout();
  const { user } = useAuth();
  const userRole = (user as { rol?: string } | undefined)?.rol ?? "";
  const isSuperAdmin = userRole === "superadmin";
  const { data: rolePermsData } = useRolePermissions();
  const userPerms: readonly string[] = isSuperAdmin
    ? ["*"]
    : (rolePermsData?.data?.[userRole] ?? ROLE_PERMISSIONS[userRole as AnyRole] ?? []);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar-collapsed") === "true");
  const [openSections, setOpenSections] = useState<Record<NavSectionId, boolean>>(() => {
    const activeSection = NAV_ITEMS.find(
      (item) => location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)
    )?.section;
    return {
      operaciones: true,
      reportes: activeSection === "reportes",
      recursos: activeSection === "recursos",
      catalogos: activeSection === "catalogos",
      sistema: activeSection === "sistema",
    };
  });

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      localStorage.setItem("sidebar-collapsed", String(!prev));
      return !prev;
    });
  };

  const handleLogout = () => {
    logoutApi(undefined, {
      onSettled: () => {
        clearTokens();
        navigate("/login");
      },
    });
  };

  const canSeeSchedule = userRole === "admin" || userRole === "logistica";
  const today = format(new Date(), "yyyy-MM-dd");

  const { enabledFlags } = useFeatureFlagsContext();
  const { data: todaySchedule } = useGetDailySchedule(
    { fecha: today },
    { query: { enabled: canSeeSchedule, refetchInterval: 60_000, staleTime: 30_000, queryKey: getGetDailyScheduleQueryKey({ fecha: today }) } }
  );
  const unassignedCount = canSeeSchedule ? (todaySchedule?.sinAsignar?.length ?? 0) : 0;
  const { hasUnread: hasUnreadNews } = useChangelog();

  const visibleItems = NAV_ITEMS.filter((item) => {
    // Feature flag gate (always applied)
    if (item.featureFlag && !enabledFlags.has(item.featureFlag)) return false;

    // Permission-based gate: when requiredPermission is set it's the sole access control
    // (replaces the role check — this is what makes the nav fully customizable)
    if (item.requiredPermission) {
      return isSuperAdmin || userPerms.includes("*") || userPerms.includes(item.requiredPermission);
    }

    // Fallback for items without requiredPermission (usuarios, roles-permisos, etc.)
    return item.roles.includes(userRole as never);
  });

  const sections = useMemo(() => {
    return NAV_SECTIONS
      .map((section) => ({
        ...section,
        items: visibleItems.filter((item) => item.section === section.id),
      }))
      .filter((section) => section.items.length > 0);
  }, [visibleItems]);

  const toggleSection = (sectionId: NavSectionId) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // User initials for avatar
  const userName = (user as { nombre?: string } | undefined)?.nombre
    ?? (user as { email?: string } | undefined)?.email
    ?? "U";
  const initials = userName.slice(0, 1).toUpperCase();
  const appVersion = __APP_VERSION__?.trim() || "N/D";

  return (
    <div className="flex h-screen mesh-bg overflow-hidden" data-feedback-capture-root="true">

      {/* ── Sidebar ──────────────────────────────────────────────────── */}
      <aside
        className={cn(
          "sidebar-glass flex-shrink-0 text-sidebar-foreground flex flex-col transition-all duration-300 ease-in-out relative z-20",
          collapsed ? "w-[60px]" : "w-[228px]"
        )}
      >
        {/* Brand + toggle */}
        <div
          className={cn(
            "flex items-center border-b border-white/[0.05]",
            collapsed ? "flex-col gap-2.5 py-4 px-2" : "gap-3 px-4 py-[14px]"
          )}
        >
          {/* Logo */}
          {collapsed ? (
            <div className="flex-shrink-0 size-8 rounded-lg overflow-hidden">
              <img src="/logo-santa-teresita.svg" alt="LST" className="h-full w-auto max-w-none" />
            </div>
          ) : (
            <div className="flex-1 min-w-0 flex items-center gap-2.5">
              <div className="h-9 w-8 shrink-0 overflow-hidden rounded-lg">
                <img
                  src="/logo-santa-teresita.svg"
                  alt="La Santa Teresita"
                  className="h-full w-auto max-w-none"
                />
              </div>
              <div className="min-w-0">
                <h1 className="text-[17px] font-semibold leading-none tracking-[-0.03em] text-white">
                  LST Flota
                </h1>
                <p className="mt-1 text-[12px] font-medium leading-none text-white/48">
                  Operaciones
                </p>
                <Link
                  to="/novedades"
                  title={hasUnreadNews ? "Hay novedades en el sistema" : "Ver novedades del sistema"}
                  className="mt-[5px] inline-flex items-center gap-1.5 text-[10px] font-medium leading-none text-white/55 tabular-nums tracking-[0.02em] transition-colors hover:text-white/85"
                >
                  v{appVersion}
                  {hasUnreadNews && (
                    <span className="relative flex size-1.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-400 opacity-75" />
                      <span className="relative inline-flex size-1.5 rounded-full bg-orange-500" />
                    </span>
                  )}
                </Link>
              </div>
            </div>
          )}

          {/* Collapse toggle */}
          <button
            onClick={toggleCollapsed}
            title={collapsed ? "Expandir menú" : "Colapsar menú"}
            className={cn(
              "flex-shrink-0 p-1.5 rounded-lg text-white/25 hover:text-white/65 hover:bg-white/[0.06] transition-all",
              collapsed && "mt-0"
            )}
          >
            {collapsed
              ? <PanelLeftOpen className="size-3.5" />
              : <PanelLeftClose className="size-3.5" />
            }
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-2 overflow-x-hidden">
          <div className={cn("space-y-3", collapsed ? "px-1.5" : "px-2") }>
            {sections.map((section) => {
              const isOpen = openSections[section.id] ?? false;

              return (
                <section key={section.id} className="space-y-1">
                  {!collapsed ? (
                    <button
                      type="button"
                      onClick={() => toggleSection(section.id)}
                      className="flex w-full items-center justify-between rounded-xl px-3 py-1.5 text-left transition-colors hover:bg-white/[0.04]"
                    >
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/30">{section.label}</p>
                      <ChevronDown className={cn("size-4 text-white/28 transition-transform", isOpen && "rotate-180") } />
                    </button>
                  ) : (
                    <div className="mx-1 my-2 border-t border-white/[0.06]" />
                  )}

                  {(collapsed || isOpen) && (
                    <ul className="space-y-[2px]">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        const isActive =
                          location.pathname === item.href ||
                          location.pathname.startsWith(`${item.href}/`);

                        return (
                          <li key={item.href}>
                            <Link
                              to={item.href}
                              title={collapsed ? item.label : undefined}
                              className={cn(
                                "group flex items-center rounded-xl text-[13px] font-medium transition-all duration-150",
                                collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5",
                                isActive
                                  ? "nav-active text-white"
                                  : "text-white/48 hover:bg-white/[0.05] hover:text-white/82"
                              )}
                            >
                              <div className="relative shrink-0">
                                <Icon
                                  className={cn(
                                    "transition-transform duration-150",
                                    collapsed ? "h-[18px] w-[18px]" : "h-[15px] w-[15px]",
                                    !isActive && "group-hover:scale-110"
                                  )}
                                />
                                {item.href === "/novedades" && hasUnreadNews && (
                                  <span className="absolute -top-1 -right-1 size-2 rounded-full bg-orange-500 ring-2 ring-black/30" />
                                )}
                                {item.href === "/programacion" && unassignedCount > 0 && (
                                  <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] bg-orange-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-[3px] leading-none">
                                    {unassignedCount > 9 ? "9+" : unassignedCount}
                                  </span>
                                )}
                              </div>
                              {!collapsed && <span className="truncate leading-tight flex-1">{item.label}</span>}
                              {!collapsed && item.href === "/programacion" && unassignedCount > 0 && (
                                <span className="ml-auto shrink-0 bg-orange-500/20 text-orange-300 text-[10px] font-semibold rounded-full px-1.5 py-0.5 leading-none">
                                  {unassignedCount}
                                </span>
                              )}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        </nav>

        {/* ── User section ─────────────────────────────────────────── */}
        <div className={cn("border-t border-white/[0.05]", collapsed ? "p-1.5" : "p-2.5")}>
          {!collapsed && (
            <div className="flex items-center gap-2.5 px-2.5 py-2 mb-1 rounded-xl">
              {/* Avatar */}
              <div className="flex-shrink-0 size-7 gradient-brand rounded-lg flex items-center justify-center text-white text-[11px] font-bold elevation-1">
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-medium text-white/72 truncate leading-tight">
                  {userName}
                </p>
                <p className="text-[10px] text-white/28 capitalize leading-tight">{userRole}</p>
              </div>
            </div>
          )}

          {/* Logout */}
          <button
            onClick={handleLogout}
            title={collapsed ? "Cerrar Sesión" : undefined}
            className={cn(
              "flex items-center w-full rounded-xl transition-all duration-150",
              "text-white/35 hover:text-red-400 hover:bg-red-500/[0.08]",
              collapsed
                ? "justify-center px-2 py-2.5"
                : "gap-2.5 px-2.5 py-2 text-[12px] font-medium"
            )}
          >
            <LogOut className="size-3.5 shrink-0" />
            {!collapsed && "Cerrar sesión"}
          </button>
        </div>
      </aside>

      {/* ── Main content ─────────────────────────────────────────────── */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-background/50">
        <div className="flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </main>

      {/* Floating feedback button */}
      <FeedbackButton />
    </div>
  );
}
